import { parseJwt } from "@connect/post-message/jwt";
import * as z from "zod/mini";
import { devBaseSchema, parseFrameConfig, themeBaseSchema } from "../../lib/parse-frame-config";
import { isSupportedWidgetFlow } from "./widget-flow";

const publicSchema = z.extend(themeBaseSchema, {
  channelId: z._default(z.string(), ""),
  flow: z._default(z.string(), ""),
  clientToken: z._default(z.string(), ""),
  quoteSignature: z._default(z.string(), ""),
  externalTransactionId: z.optional(z.string()),
  apiKey: z.optional(z.string()),
  language: z.optional(z.string()),
});

const devSchema = z.object({
  ...devBaseSchema.shape,
});

const KNOWN_KEYS = new Set([...Object.keys(publicSchema.shape), ...Object.keys(devSchema.shape)]);

const nonEmptyString = z.string().check(z.minLength(1));
const assetSchema = z.union([
  z.looseObject({ code: nonEmptyString }),
  z.looseObject({ caip19: nonEmptyString }),
]);
const executableQuoteSchema = z.looseObject({
  signature: nonEmptyString,
  wallet: z.looseObject({ address: nonEmptyString }),
  paymentMethod: z.looseObject({ type: nonEmptyString }),
  source: z.looseObject({ amount: nonEmptyString, asset: assetSchema }),
  destination: z.looseObject({ amount: nonEmptyString, asset: assetSchema }),
  executable: z.boolean(),
});
export { isSupportedWidgetFlow, type SupportedWidgetFlow } from "./widget-flow";

export type WidgetConfig = z.infer<typeof publicSchema> & z.infer<typeof devSchema>;

export function parseConfig(search?: string): WidgetConfig {
  const { config, devConfig } = parseFrameConfig({
    publicSchema,
    devSchema,
    knownKeys: KNOWN_KEYS,
    search,
  });
  return { ...config, ...devConfig };
}

export function validateWidgetConfig(config: WidgetConfig) {
  if (
    !config.channelId ||
    !isSupportedWidgetFlow(config.flow) ||
    !config.clientToken ||
    !config.quoteSignature
  ) {
    return {
      success: false,
      error: "Missing or unsupported channelId, flow, clientToken, or quoteSignature",
    } as const;
  }

  const quote = z.safeParse(executableQuoteSchema, parseJwt(config.quoteSignature));
  if (!quote.success) {
    return {
      success: false,
      error: "Invalid quote: unable to decode quote signature",
    } as const;
  }
  if (!quote.data.executable) {
    return { success: false, error: "Quote is not executable" } as const;
  }
  return { success: true, flow: config.flow } as const;
}
